import { useMemo } from 'react';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { getDataGridLocalization, getLLDate, MgPrisLocale } from '@retail/utils';
import { Box } from '@mui/material';
import { ConditionsNew } from '@retail/calculus/types';
import { AddButton, NoTableRowsOverlay, StackedTextCell } from '@retail/components';
import { ConditionsTableActions, LevelChip } from '@retail/calculus/components';
import { formatPrice } from '@retail/products/utils';
import { useTranslation } from 'react-i18next';
import { useAppTFunction } from '@retail/app/i18n';
import { getHierarchyLevelLabel } from '@retail/calculus/utils';
import { useRowColorGrouping } from '@retail/hooks';

interface Props {
  loading: boolean;
  conditions: ConditionsNew[];
  supplierLevel: boolean;
  conditionActions?: {
    setEdit: (condition: ConditionsNew) => void;
    setDeactivate: (conditionId?: number) => void;
    setDelete: (conditionId?: number) => void;
  };

  openCreateDialog?: () => void;
}

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  sortable: false,
  headerAlign: 'left',
  align: 'left',
};

export function MarkupsTable({
  conditions,
  loading,
  supplierLevel,
  conditionActions,
  openCreateDialog,
}: Props) {
  const t = useAppTFunction();
  const { i18n } = useTranslation();
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const columns: GridColDef<ConditionsNew>[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        field: 'level',
        headerName: t('markup.level'),
        width: 190,
        valueGetter: (_, row) =>
          getHierarchyLevelLabel({
            t,
            bindingType: row.bindingType,
            packageType: row.bindingTargetItem?.packageType,
          }),
        renderCell: ({ row }) => {
          return (
            <LevelChip
              bindingType={row.bindingType}
              active={!row.inDraft}
              bindingTargetItem={row.bindingTargetItem}
            />
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'supplier',
        headerName: t('markup.supplier'),
        width: 190,
        valueGetter: (_, row) => row.supplier?.supplierNumber,
        renderCell: ({ row }) => {
          return (
            <StackedTextCell
              topLine={row.supplier?.name}
              bottomLine={row.supplier?.supplierNumber}
            />
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'name',
        headerName: t('markup.levelOrItemName'),
        width: 260,
        valueGetter: (_, row) => row.bindingTargetName,
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.bindingTargetName} bottomLine={row.bindingTargetId} />
        ),
      },
      {
        ...baseColumnProps,
        field: 'coverageDegree',
        width: 100,
        headerName: t('markup.salesCoverage'),
        valueGetter: (_, row) =>
          row.markupCondition?.coverageDegree
            ? `${formatPrice(Number(row.markupCondition.coverageDegree), i18n.language)}%`
            : '-',
        renderCell: ({ row }) =>
          row.markupCondition?.coverageDegree
            ? `${formatPrice(Number(row.markupCondition.coverageDegree), i18n.language)}%`
            : '-',
      },
      {
        ...baseColumnProps,
        field: 'salesFactor',
        headerName: t('markup.salesFactor'),
        renderCell: ({ row }) =>
          row.markupCondition?.salesFactor
            ? formatPrice(Number(row.markupCondition.salesFactor), i18n.language)
            : '-',
      },
      {
        ...baseColumnProps,
        field: 'activeFromTo',
        width: 140,
        headerName: t('markup.activeFromTo'),
        valueGetter: (_, row) =>
          `${getLLDate(row.markupCondition?.validFrom)}-${
            getLLDate(row.markupCondition?.validTo) ?? ''
          }`,
        renderCell: ({ row }) => {
          const activeFrom = getLLDate(row.markupCondition?.validFrom) ?? '-';
          const activeTo = getLLDate(row.markupCondition?.validTo);
          return (
            <StackedTextCell
              topLine={activeFrom}
              bottomLine={activeTo}
              centerAlign
              nullableBottomLine
            />
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'actions',
        headerName: '',
        width: 90,
        disableColumnMenu: true,
        renderCell: ({ row }) =>
          conditionActions ? (
            <ConditionsTableActions
              handleEdit={() => conditionActions.setEdit(row)}
              handleDeactivate={
                !row.inDraft
                  ? () => conditionActions.setDeactivate(row.markupCondition?.conditionId)
                  : undefined
              }
              handleDelete={
                row.inDraft
                  ? () => conditionActions.setDelete(row.markupCondition?.conditionId)
                  : undefined
              }
            />
          ) : (
            ''
          ),
      },
    ],
    [t, i18n.language, conditionActions]
  );

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium
          getRowId={(row) => row.markupCondition!.conditionId}
          getRowClassName={getRowColorForStripedTable}
          columns={columns}
          rows={conditions}
          rowHeight={70}
          loading={loading}
          initialState={{ pinnedColumns: { right: ['actions'] } }}
          localeText={getDataGridLocalization(i18n.language as MgPrisLocale)}
          columnVisibilityModel={{ supplier: !supplierLevel }}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnReorder
          disableColumnMenu
          hideFooter
          slots={{
            noRowsOverlay: () => (
              <NoTableRowsOverlay
                noRowsText={t('markup.noMarkups')}
                addAction={
                  <AddButton onClick={openCreateDialog} disabled={!openCreateDialog}>
                    {t('markup.create.createMarkup')}
                  </AddButton>
                }
              />
            ),
          }}
        />
      </Box>
    </Box>
  );
}
